/**
 * Category Repository Implementation
 *
 * Implements the ICategoryRepository interface using Orval-generated hooks.
 * Handles all communication with the backend API.
 */

import { ICategoryRepository } from "../../domain/repositories/ICategory.repository";
import { Category } from "../../domain/entities/Category";
import { CategoryMapper, CategoryDTO } from "../mappers/Category.mapper";

/**
 * Mock data for Phase 2 - will be replaced with real API calls
 * In production, this would use Orval hooks:
 * - useAdminCategoriesControllerGetCategoryTree()
 * - useAdminCategoriesControllerCreateCategory()
 * - useAdminCategoriesControllerUpdateCategory()
 * - useAdminCategoriesControllerDeleteCategory()
 */
const MOCK_CATEGORIES: CategoryDTO[] = [
  {
    id: "1",
    name: "전체",
    parentId: null,
    level: 0,
    displayOrder: 0,
    children: [
      {
        id: "100",
        name: "사무용품",
        parentId: "1",
        level: 1,
        displayOrder: 1,
        children: [
          {
            id: "101",
            name: "필기용품",
            parentId: "100",
            level: 2,
            displayOrder: 1,
          },
          {
            id: "102",
            name: "종이류",
            parentId: "100",
            level: 2,
            displayOrder: 2,
          },
          {
            id: "103",
            name: "바인더/폴더",
            parentId: "100",
            level: 2,
            displayOrder: 3,
          },
          {
            id: "104",
            name: "클립/고정용품",
            parentId: "100",
            level: 2,
            displayOrder: 4,
          },
        ],
      },
      {
        id: "200",
        name: "전자제품",
        parentId: "1",
        level: 1,
        displayOrder: 2,
      },
      {
        id: "300",
        name: "가구",
        parentId: "1",
        level: 1,
        displayOrder: 3,
      },
    ],
  },
];

export class CategoryRepository implements ICategoryRepository {
  /**
   * Get all categories (flattened from tree structure)
   */
  async findAll(): Promise<Category[]> {
    // TODO: Replace with Orval hook call
    // const { data: response } = await useAdminCategoriesControllerGetCategoryTree()
    // return CategoryMapper.flattenTree(response.data)

    return CategoryMapper.flattenTree(MOCK_CATEGORIES);
  }

  /**
   * Get a single category by ID
   */
  async findById(id: string): Promise<Category | null> {
    const allCategories = await this.findAll();
    const found = allCategories.find((c) => c.id === id);
    return found || null;
  }

  /**
   * Get all child categories of a parent
   */
  async findByParentId(parentId: string | null): Promise<Category[]> {
    const allCategories = await this.findAll();
    return allCategories.filter((c) => c.parentId === parentId);
  }

  /**
   * Get all descendant categories (recursive)
   */
  async findDescendants(categoryId: string): Promise<Category[]> {
    const allCategories = await this.findAll();
    const descendants: Category[] = [];

    const collectDescendants = (id: string) => {
      const children = allCategories.filter((c) => c.parentId === id);
      children.forEach((child) => {
        descendants.push(child);
        collectDescendants(child.id);
      });
    };

    collectDescendants(categoryId);
    return descendants;
  }

  /**
   * Create a new category
   */
  async create(
    name: string,
    parentId: string | null,
    level: number,
  ): Promise<Category> {
    // TODO: Replace with Orval hook call
    // const { data: response } = await useAdminCategoriesControllerCreateCategory({
    //   name,
    //   parentId,
    //   level,
    //   displayOrder: 0
    // })
    // return CategoryMapper.toDomain(response.data)

    const newId = Date.now().toString();
    return new Category({
      id: newId,
      name,
      parentId,
      level,
    });
  }

  /**
   * Update a category
   */
  async update(id: string, name: string): Promise<Category> {
    // TODO: Replace with Orval hook call
    // const { data: response } = await useAdminCategoriesControllerUpdateCategory({
    //   id,
    //   data: { name }
    // })
    // return CategoryMapper.toDomain(response.data)

    const existing = await this.findById(id);
    if (!existing) {
      throw new Error(`Category with ID ${id} not found`);
    }

    return new Category({
      ...existing.toJSON(),
      name,
    });
  }

  /**
   * Delete a category
   */
  async delete(id: string): Promise<void> {
    // TODO: Replace with Orval hook call
    // await useAdminCategoriesControllerDeleteCategory({ id })

    const existing = await this.findById(id);
    if (!existing) {
      throw new Error(`Category with ID ${id} not found`);
    }
  }

  /**
   * Get the category tree structure (for hierarchical display)
   */
  async getTree(): Promise<Category[]> {
    // TODO: This might return structured tree instead of flattened list
    // For now, return flattened version
    return this.findAll();
  }
}
